import moment from 'moment';

/**
 * Range Utils
 */

export type RangeCheckFunction<T> = (value: T) => boolean;

/**
 * Parses a range and returns a function to check if a value is within the range.
 * @param range the range in string format
 * @param valueParser a parser for the range
 * @returns
 */
const getRangeFunction = <T>(
  range: string,
  valueParser: (value: string) => T,
): RangeCheckFunction<T> => {
  const [start, end] = range.split(',');
  if (!start || !end) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse number-range data');
    return () => false;
  }

  const checkLowerBound = (value: T) => {
    if (start.length === 1) return true;
    if (start.startsWith('[')) {
      return value >= valueParser(start.replace('[', ''));
    }
    return value > valueParser(start.replace('(', ''));
  };

  const checkUpperBound = (value: T) => {
    if (end.length === 1) return true;
    if (end.endsWith(']')) {
      return value <= valueParser(end.replace(']', ''));
    }
    return value < valueParser(end.replace(')', ''));
  };

  return (value) => checkLowerBound(value) && checkUpperBound(value);
};

/**
 * Parses a range and returns a function to check if a value is within the range.
 * @param range the range in string format
 * @param valueParser a parser for the range
 * @returns
 */
const getDateRangeFunction = (range: string): RangeCheckFunction<string> => {
  const [start, end] = range.split(',');
  if (!start || !end) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse date-range data');
    return () => false;
  }

  const startValue = moment(start.replace(/[[(]/, '')).startOf('date');
  const endValue = moment(end.replace(/[\])]/, '')).startOf('date');

  const checkLowerBound = (value: string) => {
    const dateValue = moment(value).startOf('date');
    if (start.length === 1) return true;
    if (start.startsWith('[')) {
      return dateValue.isSameOrAfter(startValue);
    }
    return dateValue.isAfter(startValue);
  };

  const checkUpperBound = (value: string) => {
    const dateValue = moment(value).startOf('date');
    if (end.length === 1) return true;
    if (end.endsWith(']')) {
      return dateValue.isSameOrBefore(endValue);
    }
    return dateValue.isBefore(endValue);
  };

  return (value) => checkLowerBound(value) && checkUpperBound(value);
};

/**
 * Checks if a range type is valid
 * @param rangeType the range type
 * @returns true if the range type is valid, false otherwise
 */
export const isRangeType = (rangeType: string) => {
  return (
    rangeType === 'integer' || rangeType === 'decimal' || rangeType === 'date'
  );
};

/**
 * Retrieves a range matcher (checker)
 * (value) => boolean
 * @param range
 * @param type
 * @returns
 */
export const getRangeMatcher = (
  range: string,
  type: 'integer' | 'decimal' | 'date',
): RangeCheckFunction<string | number> => {
  /* @note sadly, any is necessary in this block for the generic type to work */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  if (type === 'integer') {
    return getRangeFunction<number>(range, (v) => parseInt(v, 10)) as any;
  }
  if (type === 'decimal') {
    return getRangeFunction<number>(range, (v) => parseFloat(v)) as any;
  }
  if (type === 'date') {
    return getDateRangeFunction(range) as any;
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */
  return () => false;
};
